import axios from 'axios';
import API_BASE_URL from './apiBaseUrl';

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';

const appendFormValue = (params, key, value) => {
  if (value === undefined || value === null) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      const nextKey = isPlainObject(item) || Array.isArray(item)
        ? `${key}[${index}]`
        : `${key}[]`;
      appendFormValue(params, nextKey, item);
    });
    return;
  }

  if (isPlainObject(value)) {
    Object.entries(value).forEach(([childKey, childValue]) => {
      appendFormValue(params, `${key}[${childKey}]`, childValue);
    });
    return;
  }

  params.append(key, value instanceof Date ? value.toISOString() : String(value));
};

const toFormUrlEncoded = (payload) => {
  const params = new URLSearchParams();
  Object.entries(payload || {}).forEach(([key, value]) => {
    appendFormValue(params, key, value);
  });
  return params;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(
  (config) => {
    const method = (config.method || 'get').toLowerCase();
    const contentType = config.headers?.['Content-Type'] || config.headers?.['content-type'] || '';
    const isMultipart = typeof contentType === 'string' && contentType.includes('multipart/form-data');
    const skipFormUrlEncoded = config.skipFormUrlEncoded === true;
    const shouldEncodeBody = ['post', 'put', 'patch'].includes(method)
      && config.data
      && !(config.data instanceof FormData)
      && !(config.data instanceof URLSearchParams)
      && !isMultipart
      && !skipFormUrlEncoded;

    if (shouldEncodeBody) {
      config.data = toFormUrlEncoded(config.data);
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// A failed feature request must never erase the whole admin session. In
// particular, a permission update can overlap a navigation request; treating
// any one 401 as a logout loses the operator's current work.
// Session validity is established centrally by authStore.checkAuth() through
// /api/admin-auth/me, including on the next page load.
api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

const getAllUserPages = async (params = {}) => {
  const limit = Math.min(Math.max(Number(params.limit) || 100, 1), 100);
  const collected = [];
  let page = Math.max(Number(params.page) || 1, 1);
  let totalPages = page;
  let total = 0;
  let lastResponse = null;

  do {
    const response = await api.get('/api/admin/users', {
      params: { ...params, page, limit }
    });
    lastResponse = response;
    const pageData = response.data?.data || [];
    const pagination = response.data?.pagination || {};
    collected.push(...pageData);
    total = Number(pagination.total || collected.length);
    totalPages = Number(pagination.totalPages || page);
    page += 1;
  } while (page <= totalPages);

  return {
    ...lastResponse,
    data: {
      ...(lastResponse?.data || {}),
      count: collected.length,
      data: collected,
      pagination: {
        ...(lastResponse?.data?.pagination || {}),
        page: 1,
        limit,
        total,
        totalPages
      }
    }
  };
};

export const authAPI = {
  adminLogin: (account, password) => api.post('/api/admin-auth/login', { account, password }),
  getAdminMe: () => api.get('/api/admin-auth/me'),
  changeAdminPassword: (oldPassword, newPassword) => api.post('/api/admin-auth/change-password', { oldPassword, newPassword }),
  requestOTP: (phoneNumber) => api.post('/api/auth/request-otp', { phoneNumber }),
  login: (phoneNumber, otp) => api.post('/api/auth/verify-otp', { phoneNumber, otp }),
  loginWithPassword: (phoneNumber, password) => api.post('/api/auth/login-password', { phoneNumber, password }),
  loginWithEmail: (email, password) => api.post('/api/auth/login-email', { email, password }),
  setPassword: (password) => api.post('/api/auth/set-password', { password }),
  getProfile: () => api.get('/api/users/profile')
};

export const userAPI = {
  getProfile: () => api.get('/api/users/profile'),
  getAvailableRoles: () => api.get('/api/users/roles/available'),
  switchRole: (role) => api.post('/api/users/roles/switch', { role })
};

export const unitAPI = {
  getUnits: () => api.get('/api/admin/units'),
  getUnitDetail: (unitId, params = {}) => api.get(`/api/admin/units/${unitId}`, { params }),
  updateUnitStatus: (unitId, action) => api.post(`/api/admin/units/${unitId}/control`, { action }),
  updateUnitDetail: (unitId, data) => api.put(`/api/admin/units/${unitId}`, data, {
    skipFormUrlEncoded: true,
    headers: {
      'Content-Type': 'application/json'
    }
  }),
  updateUnitPricing: (unitId, data) => api.put(`/api/admin/units/${unitId}/pricing`, data, {
    skipFormUrlEncoded: true,
    headers: {
      'Content-Type': 'application/json'
    }
  }),
  registerDevice: (device) => api.post(
    '/api/admin/units/import',
    typeof device === 'object' ? device : { deviceNo: device }
  ),
  getDeviceQR: (deviceId) => api.get(`/api/admin/units/${encodeURIComponent(deviceId)}/qrcode`),
  createUnit: (data) => api.post('/api/admin/units', data),
  getUnitFilters: (unitId) => api.get(`/api/admin/units/${unitId}/filters`),
  resetUnitFilter: (unitId, filterKey, note = '') => api.post(`/api/admin/units/${unitId}/filters/${filterKey}/reset`, { note }),
  getWaterQualityAnalysis: (unitId) => api.get(`/api/admin/units/${unitId}/water-quality-analysis`),
  getWaterExportData: (unitId, year, month) => api.get(`/api/admin/units/${unitId}/water-export-data`, {
    params: { year, month }
  })
};

export const financeAPI = {
  getDashboardSummary: (params = {}) => api.get('/api/finance/dashboard-summary', { params }),
  getTransactions: (params = {}) => api.get('/api/finance/transactions', { params }),
  getTransactionSummary: (params = {}) => api.get('/api/finance/transactions/summary', { params }),
  getRevenue: () => api.get('/api/finance/revenue'),
  getStatement: (params = {}) => api.get('/api/finance/statement', { params }),
  getPhysicalCardSummary: () => api.get('/api/finance/physical-cards/summary'),
  getAbnormalFinanceSummary: (params = {}) => api.get('/api/finance/abnormal-summary', { params }),
  exportTransactions: (params = {}) => api.get('/api/finance/export', { params, responseType: 'blob' }),
  exportTransactionSummary: (params = {}) => api.get('/api/finance/export-summary', { params, responseType: 'blob' }),
  getBankAccounts: (params = {}) => api.get('/api/finance/bank-accounts', { params }),
  revealBankAccount: (id) => api.get(`/api/finance/bank-accounts/${id}/reveal`),
  createBankAccount: (data) => api.post('/api/finance/bank-accounts', data, { skipFormUrlEncoded: true }),
  updateBankAccount: (id, data) => api.put(`/api/finance/bank-accounts/${id}`, data, { skipFormUrlEncoded: true }),
  setDefaultBankAccount: (id) => api.post(`/api/finance/bank-accounts/${id}/default`),
  verifyBankAccount: (id) => api.post(`/api/finance/bank-accounts/${id}/verify`),
  exportBankAccounts: () => api.get('/api/finance/bank-accounts/export', { responseType: 'blob' }),
  getPayable: (params) => api.get('/api/finance/payouts/payable', { params }),
  getPayouts: (params = {}) => api.get('/api/finance/payouts', { params }),
  createPayout: (data) => api.post('/api/finance/payouts', data, { skipFormUrlEncoded: true }),
  transitionPayout: (id, data) => api.put(`/api/finance/payouts/${id}/status`, data, { skipFormUrlEncoded: true })
};

export const settingsAPI = {
  getSettings: () => api.get('/api/settings'),
  updateSettings: (key, data) => api.post(`/api/settings/${key}`, data),
  getBusinessCosts: () => api.get('/api/settings/business-costs'),
  updateBusinessCosts: (data) => api.put('/api/settings/business-costs', data, {
    skipFormUrlEncoded: true,
    headers: { 'Content-Type': 'application/json' }
  }),
  getProfitSharingDefaults: () => api.get('/api/settings/profit-sharing-defaults'),
  getGrowthProgram: () => api.get('/api/settings/growth-program/admin'),
  updateGrowthProgram: (data) => api.put('/api/settings/growth-program/admin', data, {
    skipFormUrlEncoded: true,
    headers: { 'Content-Type': 'application/json' }
  }),
  updateProfitSharingDefaults: (data) => api.put('/api/settings/profit-sharing-defaults', data, {
    skipFormUrlEncoded: true,
    headers: { 'Content-Type': 'application/json' }
  })
};

export const deviceSettingsAPI = {
  getSettings: (deviceId) => api.get(`/api/admin/device-settings/${deviceId}`),
  updateSettings: (deviceId, data) => api.post(`/api/admin/device-settings/${deviceId}`, data),
  resetStandard: (deviceId) => api.post(`/api/admin/device-settings/${deviceId}/reset-standard`),
  queryDevice: (deviceId) => api.post(`/api/admin/device-settings/${deviceId}/query`)
};

export const adminAPI = {
  getPartnerTree: () => api.get('/api/partners/tree'),
  getUnassignedStewards: () => api.get('/api/partners/unassigned-stewards'),
  bindSteward: (rpId, stewardId) => api.post('/api/partners/bind', { rpId, stewardId }),
  unbindSteward: (stewardId) => api.post('/api/partners/unbind', { stewardId }),
  getAuditLogs: (params = {}) => api.get('/api/audit', { params }),
  getAdminAccounts: (params = {}) => api.get('/api/admin/accounts', { params }),
  createAdminAccount: (data) => api.post('/api/admin/accounts', data),
  updateAdminAccount: (id, data) => api.put(`/api/admin/accounts/${id}`, data),
  resetAdminAccountPassword: (id, newPassword) => api.put(`/api/admin/accounts/${id}/password`, { newPassword }),
  archiveAdminAccount: (id) => api.delete(`/api/admin/accounts/${id}`),
  restoreAdminAccount: (id) => api.post(`/api/admin/accounts/${id}/restore`),
  getUsers: (params = {}) => api.get('/api/admin/users', { params }),
  getUsersAllPages: getAllUserPages,
  exportUsersCsv: (params = {}) => api.get('/api/admin/users/export.csv', { params, responseType: 'blob' }),
  searchUsers: (query, roles) => api.get('/api/admin/users/search', { params: { q: query, roles: roles?.join(',') } }),
  createUser: (data) => api.post('/api/admin/users', data),
  updateUserRole: (id, role) => api.put(`/api/admin/users/${id}/role`, { role }),
  updateUserRoles: (id, data) => api.put(`/api/admin/users/${id}/roles`, data),
  removeUserRole: (id, role) => api.delete(`/api/admin/users/${id}/roles/${role}`),
  updateUserPassword: (userId, newPassword) => api.put(`/api/admin/users/${userId}/password`, { newPassword }),
  getUserLogs: (userId, page = 1, limit = 10) => api.get(`/api/admin/users/${userId}/logs`, { params: { page, limit } }),
  getUserTransactions: (userId, type, page = 1, limit = 50) => api.get(`/api/admin/users/${userId}/transactions`, { params: { type, page, limit } }),
  getUserRecharges: (userId, page = 1, limit = 50) => api.get(`/api/admin/users/${userId}/recharges`, { params: { page, limit } }),
  getUserCards: (userId) => api.get(`/api/admin/users/${userId}/cards`),
  getUserAnalysis: (userId) => api.get(`/api/admin/users/${userId}/analysis`),
  getStewardKrialCards: (params = {}) => api.get('/api/admin/stewards/krial-cards', { params }),
  assignStewardKrialCard: (userId, data) => api.post(`/api/admin/stewards/${userId}/krial-card`, data),
  refreshStewardKrialCard: (userId) => api.post(`/api/admin/stewards/${userId}/krial-card/refresh`),
  // 设备绑定管理
  bindDevicesToUser: (userId, deviceIds, bindingType) => api.post(`/api/admin/users/${userId}/bind-devices`, { deviceIds, bindingType }),
  unbindDevicesFromUser: (userId, deviceIds, bindingType) => api.delete(`/api/admin/users/${userId}/unbind-devices`, { data: { deviceIds, bindingType } }),
  getUserDevices: (userId, params = {}) => api.get(`/api/admin/users/${userId}/devices`, { params }),
  searchDevices: (params) => api.get('/api/admin/devices/search', { params }),
  getCardBatches: () => api.get('/api/cards/batches'),
  generateCardBatch: (data) => api.post('/api/cards/generate', data),
  getBatchCards: (id) => api.get(`/api/cards/batches/${id}/cards`),
  getPhysicalCards: (params = {}) => api.get('/api/admin/physical-cards', { params }),
  previewPhysicalCardStatus: (data) => api.post('/api/admin/physical-cards/status/preview', data, { skipFormUrlEncoded: true }),
  changePhysicalCardStatus: (data) => api.post('/api/admin/physical-cards/status/batch', data, { skipFormUrlEncoded: true }),
  addPhysicalCard: (data) => api.post('/api/admin/physical-cards', data),
  batchAddPhysicalCards: (cards) => api.post('/api/admin/physical-cards/batch', { cards }),
  assignPhysicalCardKrialTrial: (data) => api.post('/api/admin/physical-cards/krial-trial', data),
  previewPhysicalCardBalanceOperation: (data) => api.post('/api/admin/physical-cards/balance/preview', data),
  batchWritePhysicalCardBalances: (data) => api.post('/api/admin/physical-cards/balance/batch-write', data),
  batchResetPhysicalCardBalances: (data) => api.post('/api/admin/physical-cards/balance/batch-reset', data),
  getPhysicalCardBalanceLogs: (params = {}) => api.get('/api/admin/physical-cards/balance-logs', { params }),
  getPermissions: () => api.get('/api/admin/permissions'),
  updatePermissions: (matrix) => api.post('/api/admin/permissions', { matrix }, {
    skipFormUrlEncoded: true,
    headers: {
      'Content-Type': 'application/json'
    }
  }),
  getPermissionRoles: () => api.get('/api/admin/permission-roles'),
  createPermissionRole: (payload) => api.post('/api/admin/permission-roles', payload),
  updatePermissionRole: (id, payload) => api.put(`/api/admin/permission-roles/${id}`, payload),
  deletePermissionRole: (id) => api.delete(`/api/admin/permission-roles/${id}`),
  assignAccountPermissionRoles: (accountId, permissionRoleIds) => api.put(`/api/admin/accounts/${accountId}/permission-roles`, { permissionRoleIds }),
  getInspections: (params = {}) => api.get('/api/admin/inspections', { params }),
  getInspectionDetail: (inspectionId) => api.get(`/api/admin/inspections/${inspectionId}`),
  getFilterConfigs: () => api.get('/api/admin/filter-configs'),
  updateFilterConfig: (filterKey, data) => api.put(`/api/admin/filter-configs/${filterKey}`, data),
  reviewInspection: (inspectionId, reviewNote = '') => api.post(`/api/admin/inspections/${inspectionId}/review`, { reviewNote }),
  rejectInspection: (inspectionId, rejectionReason, reviewNote = '') => api.post(`/api/admin/inspections/${inspectionId}/reject`, {
    rejectionReason,
    reviewNote
  })
};

export const simCardAPI = {
  list: (params = {}) => api.get('/api/admin/sim-cards', { params }),
  create: (data) => api.post('/api/admin/sim-cards', data),
  update: (id, data) => api.put(`/api/admin/sim-cards/${id}`, data),
  history: (id) => api.get(`/api/admin/sim-cards/${id}/history`),
  assign: (id, data) => api.post(`/api/admin/sim-cards/${id}/assign`, data),
  renew: (id, data) => api.post(`/api/admin/sim-cards/${id}/renew`, data),
  deactivate: (id, data) => api.post(`/api/admin/sim-cards/${id}/deactivate`, data)
};

export const adminCapabilityAPI = {
  get: () => api.get('/api/admin/capabilities')
};

export const stewardChangeAPI = {
  list: (params = {}) => api.get('/api/unit-steward/admin/change-requests', { params }),
  review: (id, data) => api.post(`/api/unit-steward/admin/change-requests/${id}/review`, data),
  directChange: (unitId, data) => api.post(`/api/unit-steward/admin/units/${unitId}/direct-change`, data),
  history: (unitId) => api.get(`/api/unit-steward/admin/units/${unitId}/history`)
};

export const applicationAPI = {
  getApplications: (params) => api.get('/api/applications/admin/list', { params }),
  getPendingCount: () => api.get('/api/applications/admin/pending-count'),
  reviewApplication: (id, data) => api.put(`/api/applications/admin/${id}/review`, data),
  addCommunicationLog: (id, data) => api.post(`/api/applications/admin/${id}/communication`, data)
};

export const profitSharingAPI = {
  getUnitConfig: (unitId) => api.get(`/api/profit-sharing/unit-config/${unitId}`),
  updateUnitConfig: (unitId, data) => api.put(`/api/profit-sharing/unit-config/${unitId}`, data),
  getMonthlySummary: (params) => api.get('/api/profit-sharing/monthly-summary', { params }),
  getMonthlySales: (unitId, params) => api.get(`/api/profit-sharing/monthly-sales/${unitId}`, { params }),
  getRoleSummary: (params) => api.get('/api/profit-sharing/role-summary', { params }),
  getFinanceDashboard: (params) => api.get('/api/profit-sharing/finance-dashboard', { params }),
  getUserDevices: (params) => api.get('/api/profit-sharing/user-devices', { params }),
  getFinancialDetails: (params) => api.get('/api/profit-sharing/financial-details', { params }),
  getExpenseBreakdown: (unitId, params) => api.get(`/api/profit-sharing/expense-breakdown/${unitId}`, { params })
};

export const puskudAPI = {
  getPolicy: () => api.get('/api/puskud/policy'),
  getHierarchy: () => api.get('/api/puskud/hierarchy'),
  getOperatingSummary: (params = {}) => api.get('/api/puskud/operating-summary', { params }),
  getPayoutOverview: (params = {}) => api.get('/api/puskud/payout-overview', { params }),
  getFinanceSummary: (params = {}) => api.get('/api/puskud/finance-summary', { params }),
  getRegions: () => api.get('/api/puskud/regions'),
  getPuskudUsers: () => api.get('/api/puskud/users')
};

export const withdrawalAPI = {
  getWithdrawals: (params) => api.get('/api/withdrawals', { params }),
  getStats: (params = {}) => api.get('/api/withdrawals/stats', { params }),
  approve: (id, notes = '') => api.put(`/api/withdrawals/${id}/approve`, { notes }),
  reject: (id, rejectionReason) => api.put(`/api/withdrawals/${id}/reject`, { rejectionReason }),
  complete: (id, notes = '') => api.put(`/api/withdrawals/${id}/complete`, { notes })
};

export const alertAPI = {
  getAlerts: (params) => api.get('/api/alerts', { params }),
  getStats: (params) => api.get('/api/alerts/stats', { params }),
  getDeliveryStatus: (id) => api.get(`/api/alerts/${id}/notification-deliveries`),
  resolve: (id, resolutionNotes = '') => api.put(`/api/alerts/${id}/resolve`, { resolutionNotes })
};

export const deviceOfflineAnalysisAPI = {
    getSummary: (params = {}) => api.get('/api/admin/device-offline-analysis/summary', { params }),
  getDevices: (params = {}) => api.get('/api/admin/device-offline-analysis/devices', { params }),
  getDeviceIncidents: (deviceId, params = {}) => api.get(`/api/admin/device-offline-analysis/devices/${encodeURIComponent(deviceId)}/incidents`, { params }),
  getIncident: (incidentId) => api.get(`/api/admin/device-offline-analysis/incidents/${incidentId}`),
  getIncidentLogs: (incidentId, limit = 500) => api.get(`/api/admin/device-offline-analysis/incidents/${incidentId}/logs`, { params: { limit } }),
  reanalyzeIncident: (incidentId) => api.post(`/api/admin/device-offline-analysis/incidents/${incidentId}/reanalyze`)
};

export const notificationAPI = {
  list: (params) => api.get('/api/notifications', { params }),
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
  markRead: (id) => api.put(`/api/notifications/${id}/read`),
  markAllRead: () => api.put('/api/notifications/read-all'),
  registerWebPush: (subscription) => api.post('/api/notifications/devices', {
    channel: 'web_push',
    platform: 'web',
    subscription
  }),
  revokeDevice: (id) => api.delete(`/api/notifications/devices/${id}`)
};

export const stewardAPI = {
  getSummary: () => api.get('/api/steward/summary'),
  getMyUnits: (params) => api.get('/api/steward/my-units', { params }),
  getUnitDetail: (unitId) => api.get(`/api/steward/units/${unitId}`),
  getUnitWaterQuality: (unitId, days = 7) => api.get(`/api/steward/units/${unitId}/water-quality`, { params: { days } }),
  checkIn: (payload) => api.post('/api/steward/checkin', payload),
  getCheckInHistory: (params) => api.get('/api/steward/checkin-history', { params }),
  getCheckInStatistics: (params) => api.get('/api/steward/checkin-statistics', { params }),
  getComplianceSummary: (params = {}) => api.get('/api/steward/compliance-summary', { params }),
  getCheckInDetail: (id) => api.get(`/api/steward/checkin/${id}`)
};

export const firmwareAPI = {
  uploadFirmware: (formData, onUploadProgress) => api.post('/api/firmware/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress
  }),
  getFirmwareList: (params) => api.get('/api/firmware/list', { params }),
  deleteFirmware: (firmwareId, params = {}) => api.delete(`/api/firmware/${firmwareId}`, { params }),
  getUpgradeTasks: (params) => api.get('/api/firmware/upgrades', { params }),
  triggerUpgrade: (data) => api.post('/api/firmware/upgrade', data),
  getUpgradeStatus: (taskId) => api.get(`/api/firmware/upgrade/${taskId}/status`),
  cancelUpgrade: (taskId) => api.post(`/api/firmware/upgrade/${taskId}/cancel`),
  retryUpgrade: (taskId) => api.post(`/api/firmware/upgrade/${taskId}/retry`),
  deleteUpgrade: (taskId) => api.delete(`/api/firmware/upgrade/${taskId}`)
};

export const contentAPI = {
  list: () => api.get('/api/content'),
  update: (key, text) => api.put(`/api/content/${key}`, { content: text }, {
    skipFormUrlEncoded: true,
    headers: {
      'Content-Type': 'application/json'
    }
  })
};

export default api;
